// FooterProvider.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { FooterContext, IFooterData, defaultFooterData } from '../contexts/AppContext';
import * as footerService from '../Services/footerServices';
import { linksStringToData } from '../utils/footerUtils';

interface FooterProviderProps {
  children: React.ReactNode;
}

const CACHE_TTL = 86400000; // 24 hours - footer data rarely changes

export const FooterProvider: React.FC<FooterProviderProps> = ({ children }) => {
  const [footerData, setFooterData] = useState<IFooterData>(defaultFooterData);

  const refreshFooterData = useCallback(async (forceRefresh = false) => {
    // Don't refresh if already loading
    if (footerData.isLoading && !forceRefresh) return;

    setFooterData(prev => ({ ...prev, isLoading: true }));

    try {
      const response = await footerService.getFooterData();
      
      let processedLinks = {};
      if (response?.links) {
        processedLinks = linksStringToData(response.links);
      }

      const newFooterData: IFooterData = {
        links: processedLinks,
        isLoading: false,
        lastUpdated: Date.now(),
      };

      setFooterData(newFooterData);

      // Cache to localStorage
      localStorage.setItem('footerData', JSON.stringify(processedLinks));
      localStorage.setItem('footerDataTimestamp', Date.now().toString());

    } catch (error) {
      console.error('Error refreshing footer data:', error);
      setFooterData(prev => ({
        ...prev,
        isLoading: false,
      }));
    }
  }, [footerData.isLoading]);

  // Load cached data on mount
  useEffect(() => {
    const loadCachedData = () => {
      try {
        const cachedFooterData = localStorage.getItem('footerData');
        const timestamp = localStorage.getItem('footerDataTimestamp');

        if (cachedFooterData && timestamp) {
          const lastUpdated = parseInt(timestamp);
          if (Date.now() - lastUpdated < CACHE_TTL) {
            setFooterData({
              links: JSON.parse(cachedFooterData),
              isLoading: false,
              lastUpdated,
            });
            return true;
          }
        }
      } catch (error) {
        console.error('Error loading cached footer data:', error);
      }
      return false;
    };

    if (!loadCachedData()) {
      refreshFooterData();
    }
  }, [refreshFooterData]);

  const contextValue = {
    footerData,
    setFooterData: (data: IFooterData) => setFooterData(data),
    refreshFooterData: () => refreshFooterData(true),
  };

  return (
    <FooterContext.Provider value={contextValue}>
      {children}
    </FooterContext.Provider>
  );
};